import React, { useState, useEffect, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://pathriva-api-93474716090.us-central1.run.app";
const REFRESH_BUFFER_MS = 30 * 60 * 1000;

export function getToken() {
  return sessionStorage.getItem("pathriva_token");
}

export function getUser() {
  const raw = sessionStorage.getItem("pathriva_user");
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function clearAuth() {
  sessionStorage.removeItem("pathriva_token");
  sessionStorage.removeItem("pathriva_user");
  sessionStorage.removeItem("pathriva_token_exp");
}

export function isAuthenticated() {
  const token = getToken();
  const exp = sessionStorage.getItem("pathriva_token_exp");
  if (!token || !exp) return false;
  return Date.now() < parseInt(exp, 10);
}

export function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function authFetch(path, options = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    clearAuth();
    window.location.reload();
    throw new Error("Session expired");
  }
  return res;
}

export default function Auth({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [validating, setValidating] = useState(true);
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    async function validateExisting() {
      if (!isAuthenticated()) { setValidating(false); return; }
      try {
        const res = await fetch(`${API_BASE}/api/auth/validate`, {
          method: "POST", headers: { ...authHeaders() },
        });
        const data = await res.json();
        if (data.valid) { scheduleRefresh(); onLogin(data.user); }
        else { clearAuth(); }
      } catch {}
      setValidating(false);
    }
    validateExisting();
    return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
  }, []);

  function scheduleRefresh() {
    const exp = parseInt(sessionStorage.getItem("pathriva_token_exp") || "0", 10);
    const delay = exp - REFRESH_BUFFER_MS - Date.now();
    if (delay > 0) refreshTimerRef.current = setTimeout(refreshToken, delay);
  }

  async function refreshToken() {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: "POST", headers: { ...authHeaders() },
      });
      if (!res.ok) { clearAuth(); window.location.reload(); return; }
      const data = await res.json();
      sessionStorage.setItem("pathriva_token", data.token);
      sessionStorage.setItem("pathriva_token_exp", String(Date.now() + data.expires_in * 1000));
      scheduleRefresh();
    } catch {}
  }

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) { setError("Email is required"); return; }
    if (!password) { setError("Password is required"); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Login failed"); setLoading(false); return; }

      sessionStorage.setItem("pathriva_token", data.token);
      sessionStorage.setItem("pathriva_user", JSON.stringify(data.user));
      sessionStorage.setItem("pathriva_token_exp", String(Date.now() + data.expires_in * 1000));
      scheduleRefresh();
      onLogin(data.user);
    } catch (err) {
      setError("Unable to connect to server. Please try again.");
    }
    setLoading(false);
  }

  if (validating) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ color: "#4a6175", fontSize: 12, textAlign: "center" }}>Validating session...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <div style={{ width: 36, height: 36, background: "linear-gradient(135deg, #00e5ff, #7c3aed)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: "#000" }}>P</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, color: "#e6edf3" }}>Pathriva</div>
            <div style={{ fontSize: 11, color: "#4a6175", letterSpacing: 1 }}>From prescription to proof</div>
          </div>
        </div>

        <h2 style={{ fontSize: 16, fontWeight: 600, color: "#e6edf3", marginBottom: 20, marginTop: 0 }}>Sign in to your account</h2>

        {error && (
          <div style={{ background: "rgba(255,77,109,0.1)", border: "1px solid rgba(255,77,109,0.3)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#ff4d6d", marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div>
          <label style={styles.label}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin(e)} placeholder="you@company.com" autoComplete="email" autoFocus disabled={loading} style={styles.input} />

          <label style={styles.label}>Password</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
            <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin(e)} placeholder="••••••••••••" autoComplete="current-password" disabled={loading} style={{ ...styles.input, marginBottom: 0, flex: 1 }} />
            <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ background: "#1e2d3d", border: "none", borderRadius: 6, color: "#8b949e", padding: "10px 12px", fontSize: 11, cursor: "pointer" }} tabIndex={-1}>
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          <button onClick={handleLogin} disabled={loading || !email.trim() || !password} style={{ ...styles.submitBtn, opacity: loading || !email.trim() || !password ? 0.5 : 1, cursor: loading ? "not-allowed" : "pointer" }}>
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </div>

        <p style={{ fontSize: 10, color: "#4a6175", textAlign: "center", marginTop: 20, marginBottom: 0 }}>
          Protected health information is encrypted in transit and at rest.
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#060810", fontFamily: "'Inter', -apple-system, sans-serif", padding: 20 },
  card: { width: "100%", maxWidth: 400, background: "#0d1117", border: "1px solid #1e2d3d", borderRadius: 12, padding: 32 },
  label: { display: "block", fontSize: 12, color: "#8b949e", marginBottom: 6, fontWeight: 500 },
  input: { width: "100%", padding: "10px 12px", background: "#131920", border: "1px solid #1e2d3d", borderRadius: 8, color: "#e6edf3", fontSize: 13, fontFamily: "inherit", marginBottom: 16, outline: "none", boxSizing: "border-box" },
  submitBtn: { width: "100%", padding: "11px", background: "linear-gradient(135deg, #00e5ff, #0099cc)", border: "none", borderRadius: 8, color: "#000", fontSize: 13, fontWeight: 700, fontFamily: "inherit", letterSpacing: 0.5 },
};
